import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  GroupExtensionsFilters,
  GroupCreateInput,
  GroupExtensionCreateInput,
  GroupExtensionUpdateInput,
} from '@/modules/users/types/group'
import * as groupsApi from '@/modules/users/api/groupsApi'

/**
 * Query keys for group management
 */
export const groupsKeys = {
  all: ['groups'] as const,
  lists: () => [...groupsKeys.all, 'list'] as const,
  list: (filters?: GroupExtensionsFilters) => [...groupsKeys.lists(), { filters }] as const,
  allExtensions: () => [...groupsKeys.all, 'all-extensions'] as const,
  details: () => [...groupsKeys.all, 'detail'] as const,
  detail: (id: number) => [...groupsKeys.details(), id] as const,
  laboratorios: () => [...groupsKeys.all, 'laboratorios'] as const,
  clientes: () => [...groupsKeys.all, 'clientes'] as const,
}

/**
 * Hook to get paginated group extensions list
 */
export function useGroupExtensions(filters?: GroupExtensionsFilters) {
  return useQuery({
    queryKey: groupsKeys.list(filters),
    queryFn: () => groupsApi.getGroupExtensions(filters),
  })
}

/**
 * Hook to get all group extensions without pagination
 */
export function useAllGroupExtensions() {
  return useQuery({
    queryKey: groupsKeys.allExtensions(),
    queryFn: () => groupsApi.getAllGroupExtensions(),
  })
}

/**
 * Hook to get single group extension
 */
export function useGroupExtension(extensionId: number) {
  return useQuery({
    queryKey: groupsKeys.detail(extensionId),
    queryFn: () => groupsApi.getGroupExtension(extensionId),
    enabled: !!extensionId,
  })
}

/**
 * Hook to get only laboratorios
 */
export function useLaboratorios() {
  return useQuery({
    queryKey: groupsKeys.laboratorios(),
    queryFn: () => groupsApi.getLaboratorios(),
  })
}

/**
 * Hook to get only clientes (laboratorios con cliente=true)
 */
export function useClientes() {
  return useQuery({
    queryKey: groupsKeys.clientes(),
    queryFn: () => groupsApi.getClientes(),
  })
}

/**
 * Hook to create new Django Group with its GroupExtension
 */
export function useCreateGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: GroupCreateInput) => groupsApi.createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      toast.success('Grupo creado exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear grupo')
    },
  })
}

/**
 * Hook to create group extension
 */
export function useCreateGroupExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: GroupExtensionCreateInput) => groupsApi.createGroupExtension(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      toast.success('Extensión de grupo creada exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear extensión de grupo')
    },
  })
}

/**
 * Hook to update group extension
 */
export function useUpdateGroupExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      extensionId,
      data,
    }: {
      extensionId: number
      data: GroupExtensionUpdateInput
    }) => groupsApi.updateGroupExtension(extensionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.detail(variables.extensionId) })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      toast.success('Extensión de grupo actualizada exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar extensión de grupo')
    },
  })
}

/**
 * Hook to delete group extension
 */
export function useDeleteGroupExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (extensionId: number) => groupsApi.deleteGroupExtension(extensionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      toast.success('Extensión de grupo eliminada exitosamente')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar extensión de grupo')
    },
  })
}

/**
 * Hook to toggle cliente status
 */
export function useToggleCliente() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (extensionId: number) => groupsApi.toggleCliente(extensionId),
    onSuccess: (_, extensionId) => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.detail(extensionId) })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.clientes() })
      toast.success('Estado de cliente actualizado')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al cambiar estado de cliente')
    },
  })
}

/**
 * Hook to toggle laboratorio status
 */
export function useToggleLaboratorio() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (extensionId: number) => groupsApi.toggleLaboratorio(extensionId),
    onSuccess: (_, extensionId) => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.detail(extensionId) })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.laboratorios() })
      toast.success('Estado de laboratorio actualizado')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al cambiar estado de laboratorio')
    },
  })
}

/**
 * Hook to toggle permiso status
 */
export function useTogglePermiso() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (extensionId: number) => groupsApi.togglePermiso(extensionId),
    onSuccess: (_, extensionId) => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: groupsKeys.detail(extensionId) })
      queryClient.invalidateQueries({ queryKey: groupsKeys.allExtensions() })
      toast.success('Permiso especial actualizado')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al cambiar permiso especial')
    },
  })
}
